import { useState, useMemo } from 'react';
import { useLang } from '../../config/i18n';
import { MONTHS } from '../../config/constants';
import { calcSplit } from '../../lib/helpers';
import { Modal, Field, SaveBtn, MoneyBox, SplitPreview } from '../shared/UI';

export default function PartnershipsTab({ partners, setPartners, ownership, setOwnership, units, monthly }){
  const {S, lang, dir, font} = useLang();
  const T = S.partnerships;
  const [viewSection,  setViewSection]  = useState("partners");   // "partners" | "units" | "reports"
  const [editPartner,  setEditPartner]  = useState(null);
  const [partnerForm,  setPartnerForm]  = useState({});
  const [editUnit,     setEditUnit]     = useState(null);
  const [unitOwnForm,  setUnitOwnForm]  = useState({});
  const [reportMonth,  setReportMonth]  = useState("2026-04");

  // ── Partners CRUD ──
  function openNewPartner(){ setPartnerForm({id:`p${Date.now()}`,name:"",phone:"",notes:""}); setEditPartner("new"); }
  function openEditPartner(p){ setPartnerForm({...p}); setEditPartner(p.id); }
  function savePartner(){
    if(editPartner==="new") setPartners(p=>[...p,partnerForm]);
    else setPartners(p=>p.map(x=>x.id===partnerForm.id?partnerForm:x));
    setEditPartner(null);
  }
  function deletePartner(id){
    setPartners(p=>p.filter(x=>x.id!==id));
    setOwnership(o=>{ const n={...o}; Object.keys(n).forEach(k=>{ n[k]={...n[k],splits:n[k].splits.filter(s=>s.partnerId!==id)}; }); return n; });
  }

  // ── Unit Ownership ──
  function openUnitOwn(u){
    const cfg = ownership[u.id] || { managementFeePercent:15, splits:[{type:"me",name:"FindRE",sharePercent:100}] };
    setUnitOwnForm({ unitId:u.id, unitLabel:`${u.unitNumber} — ${u.district}`, managementFeePercent:cfg.managementFeePercent, splits: cfg.splits.map(s=>({...s})) });
    setEditUnit(u.id);
  }
  function addSplitRow(){
    setUnitOwnForm(f=>({...f, splits:[...f.splits,{partnerId:partners[0]?.id||"",sharePercent:0}]}));
  }
  function removeSplitRow(i){
    setUnitOwnForm(f=>({...f, splits:f.splits.filter((_,idx)=>idx!==i)}));
  }
  function updateSplitRow(i, field, val){
    setUnitOwnForm(f=>({...f, splits:f.splits.map((s,idx)=>idx===i?{...s,[field]:field==="sharePercent"?Number(val):val}:s)}));
  }
  function saveUnitOwn(){
    const total=unitOwnForm.splits.reduce((a,s)=>a+Number(s.sharePercent||0),0);
    if(total!==100){ alert(T.must100); return; }
    setOwnership(o=>({...o,[unitOwnForm.unitId]:{managementFeePercent:Number(unitOwnForm.managementFeePercent),splits:unitOwnForm.splits}}));
    setEditUnit(null);
  }

  // ── Report Data ──
  const reportData = useMemo(()=>{
    const rows=[];
    units.forEach(u=>{
      const cfg=ownership[u.id];
      if(!cfg) return;
      const op=monthly[`${u.id}-${reportMonth}`]||{};
      const gross=Number(op.rentCollected)||0;
      const calc=calcSplit(gross,cfg,cfg.splits);
      rows.push({ unit:u, op, calc, cfg });
    });
    return rows;
  },[units,ownership,monthly,reportMonth]);

  const partnerTotals = useMemo(()=>{
    const totals={me:{name:"FindRE (أنا)",total:0,fee:0}};
    partners.forEach(p=>{ totals[p.id]={name:p.name,total:0}; });
    reportData.forEach(({calc})=>{
      totals["me"].total += calc.findreTotal;
      totals["me"].fee   += calc.fee;
      Object.entries(calc.shares).forEach(([k,v])=>{ if(k!=="me"&&totals[k]) totals[k].total+=v; });
    });
    return totals;
  },[reportData,partners]);

  const sBtn=(key,lbl)=>(
    <button onClick={()=>setViewSection(key)} style={{padding:"7px 18px",borderRadius:10,border:"1px solid",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
      background:viewSection===key?"rgba(167,139,250,0.18)":"rgba(255,255,255,0.03)",
      color:viewSection===key?"#a78bfa":"#64748b",
      borderColor:viewSection===key?"rgba(167,139,250,0.4)":"rgba(255,255,255,0.07)"}}>
      {lbl}
    </button>
  );

  return(
    <div style={{padding:"24px 28px",maxWidth:1200,margin:"0 auto"}}>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:20,fontWeight:800,color:"#c8a03c",margin:0}}>{T.title}</h2>
        <p style={{fontSize:13,color:"#475569",margin:"4px 0 0"}}>{T.subtitle}</p>
      </div>

      {/* Sub-nav */}
      <div style={{display:"flex",gap:8,marginBottom:24,borderBottom:"1px solid rgba(255,255,255,0.06)",paddingBottom:16}}>
        {sBtn("partners",T.partnersTab)}
        {sBtn("units",T.unitsTab)}
        {sBtn("reports",T.reportsTab)}
      </div>

      {/* ── PARTNERS LIST ── */}
      {viewSection==="partners"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <span style={{fontSize:14,color:"#94a3b8"}}>{T.listTitle}</span>
            <button onClick={openNewPartner} style={{padding:"7px 16px",borderRadius:8,background:"linear-gradient(135deg,#a78bfa,#7c3aed)",border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.addPartner}</button>
          </div>
          {partners.length===0&&<div style={{textAlign:"center",padding:40,color:"#334155"}}>{T.noPartners}</div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
            {partners.map(p=>{
              const unitCount=Object.values(ownership).filter(o=>o.splits.some(s=>s.partnerId===p.id)).length;
              return(
                <div key={p.id} style={{background:"rgba(167,139,250,0.05)",border:"1px solid rgba(167,139,250,0.18)",borderRadius:14,padding:"16px 18px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontSize:16,fontWeight:800,color:"#e2e8f0"}}>{p.name}</div>
                      {p.phone&&<div style={{fontSize:12,color:"#64748b",direction:"ltr",textAlign:"right",marginTop:2}}>{p.phone}</div>}
                    </div>
                    <span style={{fontSize:11,background:"rgba(167,139,250,0.15)",color:"#a78bfa",padding:"2px 9px",borderRadius:8,fontWeight:700}}>{unitCount} وحدة</span>
                  </div>
                  {p.notes&&<div style={{fontSize:12,color:"#475569",marginBottom:10,lineHeight:1.5}}>{p.notes}</div>}
                  {/* Units this partner owns */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                    {Object.entries(ownership).filter(([,o])=>o.splits.some(s=>s.partnerId===p.id)).map(([uid,o])=>{
                      const u=units.find(x=>x.id===uid);
                      const share=o.splits.find(s=>s.partnerId===p.id)?.sharePercent||0;
                      return u?<span key={uid} style={{fontSize:11,color:"#c8a03c",background:"rgba(200,160,60,0.1)",padding:"2px 8px",borderRadius:6}}>{u.unitNumber} · {share}٪</span>:null;
                    })}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>openEditPartner(p)} style={{flex:1,padding:"5px",borderRadius:7,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.2)",color:"#c8a03c",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>✏️ تعديل</button>
                    <button onClick={()=>deletePartner(p.id)} style={{padding:"5px 10px",borderRadius:7,background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.deleteBtn}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── UNIT OWNERSHIP ── */}
      {viewSection==="units"&&(
        <div>
          <div style={{fontSize:13,color:"#94a3b8",marginBottom:16}}>{T.unitsSubtitle}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
            {units.map(u=>{
              const cfg=ownership[u.id];
              const hasSplits=cfg&&cfg.splits.length>1;
              return(
                <div key={u.id} style={{background:hasSplits?"rgba(167,139,250,0.05)":"rgba(255,255,255,0.02)",border:`1px solid ${hasSplits?"rgba(167,139,250,0.2)":"rgba(255,255,255,0.07)"}`,borderRadius:14,padding:"14px 16px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div>
                      <span style={{fontSize:16,fontWeight:800,color:"#c8a03c"}}>{u.unitNumber}</span>
                      <span style={{fontSize:12,color:"#64748b",marginRight:8}}>{u.district}</span>
                    </div>
                    {cfg&&<span style={{fontSize:11,color:"#94a3b8",background:"rgba(255,255,255,0.04)",padding:"2px 8px",borderRadius:6}}>رسوم {cfg.managementFeePercent}٪</span>}
                  </div>
                  {cfg?.splits?.length>0?(
                    <div style={{marginBottom:10}}>
                      {cfg.splits.map((s,i)=>{
                        const name=s.type==="me"?"FindRE (أنا)":partners.find(p=>p.id===s.partnerId)?.name||"—";
                        return<div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#94a3b8",padding:"3px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}><span>{name}</span><span style={{color:"#c8a03c",fontWeight:700}}>{s.sharePercent}٪</span></div>;
                      })}
                    </div>
                  ):<div style={{fontSize:12,color:"#334155",marginBottom:10}}>{T.fullOwnership}</div>}
                  <button onClick={()=>openUnitOwn(u)} style={{width:"100%",padding:"6px",borderRadius:8,background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.22)",color:"#a78bfa",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.editOwnership}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── REPORTS ── */}
      {viewSection==="reports"&&(
        <div>
          {/* Month picker */}
          <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap"}}>
            {MONTHS.map(m=>(
              <button key={m.key} onClick={()=>setReportMonth(m.key)} style={{padding:"6px 14px",borderRadius:10,border:"1px solid",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
                background:reportMonth===m.key?"rgba(200,160,60,0.18)":"rgba(255,255,255,0.03)",
                color:reportMonth===m.key?"#c8a03c":"#64748b",
                borderColor:reportMonth===m.key?"rgba(200,160,60,0.4)":"rgba(255,255,255,0.07)"}}>
                {m.short}
              </button>
            ))}
          </div>

          {/* Partner Totals */}
          <div style={{display:"flex",gap:12,marginBottom:22,flexWrap:"wrap"}}>
            {Object.entries(partnerTotals).map(([key,p])=>(
              <div key={key} style={{background:key==="me"?"rgba(200,160,60,0.08)":"rgba(167,139,250,0.07)",border:`1px solid ${key==="me"?"rgba(200,160,60,0.22)":"rgba(167,139,250,0.2)"}`,borderRadius:14,padding:"16px 20px",flex:1,minWidth:180,textAlign:"right"}}>
                <div style={{fontSize:12,color:"#64748b",marginBottom:6}}>{p.name}</div>
                <div style={{fontSize:24,fontWeight:800,color:key==="me"?"#c8a03c":"#a78bfa",lineHeight:1}}>{p.total.toLocaleString()} <span style={{fontSize:13,fontWeight:400}}>ريال</span></div>
                {key==="me"&&p.fee>0&&<div style={{fontSize:11,color:"#64748b",marginTop:4}}>منها رسوم إدارة: {p.fee.toLocaleString()} ر</div>}
              </div>
            ))}
          </div>

          {/* Per-Unit Breakdown */}
          {reportData.length===0&&<div style={{textAlign:"center",padding:40,color:"#334155"}}>{T.noUnitsMsg}</div>}
          {reportData.map(({unit:u,op,calc,cfg})=>(
            <div key={u.id} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,flexWrap:"wrap",gap:8}}>
                <div>
                  <span style={{fontSize:18,fontWeight:800,color:"#c8a03c"}}>{u.unitNumber}</span>
                  <span style={{fontSize:13,color:"#64748b",marginRight:10}}>{u.district}</span>
                  {op.tenantName&&<span style={{fontSize:13,color:"#94a3b8"}}>· {op.tenantName}</span>}
                </div>
                <span style={{fontSize:12,color:"#64748b",background:"rgba(255,255,255,0.04)",padding:"3px 10px",borderRadius:8}}>رسوم إدارة {cfg.managementFeePercent}٪</span>
              </div>

              {/* Flow: Gross → Fee → Net → Splits */}
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:14}}>
                <MoneyBox label={T.gross} amount={calc.gross} color="#e2e8f0"/>
                <span style={{color:"#334155",fontSize:18}}>−</span>
                <MoneyBox label={`رسوم الإدارة ${cfg.managementFeePercent}٪`} amount={calc.fee} color="#c8a03c" sub="تذهب لـ FindRE"/>
                <span style={{color:"#334155",fontSize:18}}>=</span>
                <MoneyBox label={T.net} amount={calc.net} color="#38bdf8"/>
              </div>

              {/* Split rows */}
              <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:12}}>
                <div style={{fontSize:11,color:"#475569",marginBottom:8,fontWeight:700}}>{T.netDist}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {cfg.splits.map((s,i)=>{
                    const name=s.type==="me"?"FindRE (أنا)":partners.find(p=>p.id===s.partnerId)?.name||"—";
                    const amt=calc.shares[s.type==="me"?"me":s.partnerId]||0;
                    const isMe=s.type==="me";
                    return(
                      <div key={i} style={{background:isMe?"rgba(200,160,60,0.08)":"rgba(167,139,250,0.07)",border:`1px solid ${isMe?"rgba(200,160,60,0.2)":"rgba(167,139,250,0.18)"}`,borderRadius:10,padding:"10px 14px",flex:1,minWidth:160,textAlign:"right"}}>
                        <div style={{fontSize:11,color:"#64748b",marginBottom:4}}>{name} · {s.sharePercent}٪</div>
                        <div style={{fontSize:18,fontWeight:800,color:isMe?"#c8a03c":"#a78bfa"}}>{amt.toLocaleString()} <span style={{fontSize:11,fontWeight:400}}>ر</span></div>
                        {isMe&&calc.fee>0&&<div style={{fontSize:10,color:"#64748b",marginTop:3}}>+ {calc.fee.toLocaleString()} رسوم = {calc.findreTotal.toLocaleString()} ر إجمالاً</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Partner modal */}
      {editPartner&&(
        <Modal title={editPartner==="new"?T.addPartnerModal:T.editPartner} onClose={()=>setEditPartner(null)}>
          <Field label={T.fullName} value={partnerForm.name||""} onChange={v=>setPartnerForm(f=>({...f,name:v}))} required/>
          <Field label={T.phone} value={partnerForm.phone||""} onChange={v=>setPartnerForm(f=>({...f,phone:v}))}/>
          <Field label={T.notes} value={partnerForm.notes||""} onChange={v=>setPartnerForm(f=>({...f,notes:v}))} multiline rows={2}/>
          <SaveBtn onClick={savePartner} disabled={!partnerForm.name}/>
        </Modal>
      )}

      {/* Unit ownership modal */}
      {editUnit && unitOwnForm.unitId && (
        <Modal title={`ملكية وحدة ${unitOwnForm.unitLabel}`} onClose={()=>setEditUnit(null)} wide>
          <Field label={T.mgmtFeeField} value={String(unitOwnForm.managementFeePercent||15)} onChange={v=>setUnitOwnForm(f=>({...f,managementFeePercent:Number(v)}))} type="number"/>

          <div style={{fontSize:12,color:"#94a3b8",marginBottom:10,fontWeight:700}}>{T.splitTitle}</div>

          {unitOwnForm.splits?.map((s,i)=>{
            const isMe=s.type==="me";
            return(
              <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,background:"rgba(255,255,255,0.03)",padding:"10px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.06)"}}>
                <div style={{flex:1,fontSize:14,color:"#e2e8f0",fontWeight:600}}>
                  {isMe?"🏢 FindRE (أنا)":(
                    <select value={s.partnerId||""} onChange={e=>updateSplitRow(i,"partnerId",e.target.value)}
                      style={{width:"100%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,padding:"6px 10px",color:"#e2e8f0",fontSize:13,outline:"none",direction:"rtl",fontFamily:"'Tajawal',sans-serif"}}>
                      <option value="">{T.choosePartner}</option>
                      {partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <input type="number" min="0" max="100" value={s.sharePercent} onChange={e=>updateSplitRow(i,"sharePercent",e.target.value)}
                    style={{width:64,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,padding:"6px 8px",color:"#c8a03c",fontSize:14,fontWeight:700,outline:"none",textAlign:"center",fontFamily:"'Tajawal',sans-serif"}}/>
                  <span style={{color:"#64748b",fontSize:14}}>٪</span>
                </div>
                {!isMe&&<button onClick={()=>removeSplitRow(i)} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",borderRadius:7,padding:"5px 8px",cursor:"pointer",fontSize:13,fontFamily:"'Tajawal',sans-serif"}}>✕</button>}
              </div>
            );
          })}

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <button onClick={addSplitRow} style={{padding:"6px 14px",borderRadius:8,background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.25)",color:"#a78bfa",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.addPartnerRow}</button>
            <span style={{fontSize:13,color: unitOwnForm.splits?.reduce((a,s)=>a+Number(s.sharePercent||0),0)===100?"#22c55e":"#ef4444",fontWeight:700}}>
              المجموع: {unitOwnForm.splits?.reduce((a,s)=>a+Number(s.sharePercent||0),0)}٪
            </span>
          </div>

          {/* Preview */}
          {unitOwnForm.splits?.reduce((a,s)=>a+Number(s.sharePercent||0),0)===100&&(
            <SplitPreview
              splits={unitOwnForm.splits}
              managementFeePercent={unitOwnForm.managementFeePercent}
              partners={partners}
              calcSplit={calcSplit}
            />
          )}

          <SaveBtn onClick={saveUnitOwn}/>
        </Modal>
      )}
    </div>
  );
}
